/* eslint-disable @typescript-eslint/no-unused-vars */
import { Entity, SystemInterface, XY } from '@goodboydigital/odie';

import { Settings } from '../../AppConfig';
import { Camera25D } from '../../display/odie25d/View25DSystem';
import { Maths } from '../../utils/Maths';
import { Random } from '../../utils/Random';
import { Game } from '../Game';

export enum CameraMode
    {
    NORMAL,
    SIMPLE,
    STATIC,
}

export class CameraSystem implements SystemInterface
{
    public game: Game;
    public target: XY;
    public targetPosition: XY;
    public cameraModifier: XY;
    public time = 0;
    public changedTarget = false;
    public speedModifier = false;
    public zoomModifier = 0;
    public mode: CameraMode = CameraMode.NORMAL;
    public velocity: XY;
    public left = 0;
    public top = 0;
    public right = 0;
    public bottom = 0;
    public crashPoint: XY;
    public shakeModifier: number;

    constructor(entity: Entity)
    {
        this.game = entity.scene as Game;
        this.target = { x: 0, y: 0 };
        this.targetPosition = { x: 0, y: 0 };
        this.cameraModifier = { x: 0, y: 0 };
        this.velocity = { x: 0, y: 0 };
        this.crashPoint = { x: 0, y: 0 };
        this.shakeModifier = 0;

        this.game.physics.physicsFixedUpdateRunner.add(this as any);
        this.game.physics.physicsFreeUpdateRunner.add(this as any);
        this.game.onCrash.connect(this.onCrash.bind(this));

        const mode = Settings.camera;

        if (mode === 'normal') this.mode = CameraMode.NORMAL;
        if (mode === 'simple') this.mode = CameraMode.SIMPLE;
        if (mode === 'static') this.mode = CameraMode.STATIC;
    }

    private onCrash(): void
    {
        this.setTarget(this.game.vehicle.character.physics);
        this.crashPoint.x = this.game.vehicle.character.physics.x;
        this.crashPoint.y = this.game.vehicle.character.physics.y;
        this.shakeModifier = 30;
    }

    public get camera(): Camera25D
    {
        return this.game.sceneView.camera;
    }

    public resetTarget(target: XY, zoomModifier = 1): void
    {
        this.target = target;
        this.camera.x = this.target.x;
        this.camera.y = this.target.y;
        this.targetPosition.x = this.target.x;
        this.targetPosition.y = this.target.y;
        this.zoomModifier = zoomModifier;
        this.camera.zoom = 1 + zoomModifier;
        this.cameraModifier.x = 0;
        this.cameraModifier.y = 125;
        this.velocity.x = 0;
        this.velocity.y = 0;
    }

    public setTarget(target: XY, speedModifier = false): void
    {
        this.speedModifier = speedModifier;
        this.changedTarget = target !== this.target;
        this.target = target;
        this.zoomModifier = 0;
    }

    public physicsFixedUpdate(delta = 1): void
    {
        switch (this.mode)
        {
            case CameraMode.NORMAL:
                this.updateModeNormal(delta);
                break;
            case CameraMode.SIMPLE:
                this.updateModeSimple(delta);
                break;
            case CameraMode.STATIC:
                this.updateModeStatic(delta);
                break;
        }
    }

    public updateModeNormal(delta = 1): void
    {
        // Skip a update in case of changed targets
        if (this.changedTarget)
        {
            this.changedTarget = false;

            return;
        }

        // Force a specific camera behaviour if player has crashed after finish line
        if (this.game.vehicle.finished && !this.game.vehicle.assembled)
        {
            this.camera.x += this.velocity.x;
            this.camera.y += this.velocity.y;
            this.camera.x = Maths.lerpMaxStep(this.camera.x, this.game.level.courseEndX + 200, 0.1 * delta, 1);
            this.camera.y = Maths.lerpMaxStep(this.camera.y, this.game.level.courseEndY, 0.1 * delta, 1);
            this.velocity.x = Maths.lerpMaxStep(this.velocity.x, 0, 0.1 * delta, 2);
            this.velocity.y = Maths.lerpMaxStep(this.velocity.y, 0, 0.1 * delta, 2);

            this.updateShake();
            this.updateBounds();

            return;
        }

        if (!this.game.playing)
        {
            this.targetPosition.x = this.game.vehicle.character.x;
            // this.targetPosition.y = this.game.vehicle.character.y;
            this.targetPosition.y = -100;
            
            var targetX = this.targetPosition.x + (this.game.spineDebug?.offset ?? 0) + this.cameraModifier.x;
            var targetY = this.targetPosition.y + this.cameraModifier.y;
            this.camera.x = Maths.lerp(this.camera.x, targetX, 0.2 * delta);
            this.camera.y = Maths.lerp(this.camera.y, targetY, 0.2 * delta);
            
            this.velocity.x = 0;
            this.velocity.y = 0;
            this.camera.zoom = 1.5;

            this.updateShake();
            this.updateBounds();

            return;
        }

        const tx = this.target.x;
        let ty = this.target.y;

        if (this.game.vehicle && !this.game.vehicle.assembled)
        {
            if (ty > this.crashPoint.y) ty = this.crashPoint.y;
        }

        this.velocity.x = Maths.clamp(tx - this.targetPosition.x, -20, 20);
        this.velocity.y = Maths.clamp(ty - this.targetPosition.y, -20, 20);
        this.targetPosition.x = tx;
        this.targetPosition.y = ty;

        const cameraIncrX = this.velocity.x * 5.5;
        const cameraIncrY = this.velocity.y * 1.9;
        const cameraTargetX = Maths.lerp(this.camera.x, tx, 0.2 * delta);
        const cameraTargetY = Maths.lerp(this.camera.y, ty, 0.2 * delta);

        this.cameraModifier.x = Maths.lerp(this.cameraModifier.x, cameraIncrX, 0.1 * delta);
        this.cameraModifier.y = Maths.lerp(this.cameraModifier.y, cameraIncrY, 0.1 * delta);
        this.camera.x = cameraTargetX;
        this.camera.y = cameraTargetY;

        if (this.speedModifier)
        {
            this.camera.x += this.cameraModifier.x;
            this.camera.y += this.cameraModifier.y;
        }

        const zoomFactor = ((Math.abs(this.velocity.x)) * 0.014) + ((Math.abs(this.velocity.y)) * 0.005);
        const zoom = Maths.clamp(1.2 - zoomFactor, 0.7, 1.2);

        this.camera.zoom = Maths.lerp(this.camera.zoom, zoom, 0.1 * delta) + this.zoomModifier;

        this.updateShake();
        this.updateBounds();
    }

    private updateShake(): void
    {
        if (this.shakeModifier)
        {
            this.shakeModifier *= 0.9;
            if (this.shakeModifier < 5) this.shakeModifier = 0;
            this.game.view25d.container.x = Random.range(-this.shakeModifier, this.shakeModifier);
            this.game.view25d.container.y = Random.range(-this.shakeModifier, this.shakeModifier);
        }
        else
        {
            this.game.view25d.container.x = 0;
            this.game.view25d.container.y = 0;
        }
    }

    private updateBounds(): void
    {
        const scale = this.camera.z + this.camera.zoom;
        const hor = ((this.game.width / scale) / 2) + 300;
        const ver = ((this.game.height / scale) / 2) + 200;

        this.left = -hor;
        this.right = hor;
        this.top = -ver;
        this.bottom = ver;
    }

    public updateModeSimple(delta = 1): void
    {
        this.camera.x = this.target.x;
        this.camera.y = this.target.y;

        const hor = this.game.width / 2;
        const ver = this.game.height / 2;

        this.left = -hor;
        this.right = hor;
        this.top = -ver;
        this.bottom = ver;
    }

    public updateModeStatic(delta = 1): void
    {
        // this.camera.x = (2000 * Math.floor(this.target.x / 2000)) + 1000;
        // this.camera.y = (1800 * Math.floor(this.target.y / 1800)) + 900;

        const dx = this.target.x - this.camera.x;
        const dy = this.target.y - this.camera.y;

        if (Math.abs(dx) > 2000) this.camera.x = this.target.x + (dx * 0.9);
        if (Math.abs(dy) > 800) this.camera.y = this.target.y;

        this.camera.zoom = 0.3;

        const hor = 1000;
        const ver = 500;

        this.left = -hor;
        this.right = hor;
        this.top = -ver;
        this.bottom = ver;
    }

    public resize(): void
    {
        //
    }
}
